import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import './AndroidPackagePanel.css';

const POLL_MS = 15000;
const ACTIVE = new Set(['QUEUED', 'RUNNING', 'VERIFYING']);
const REVIEW_LABELS = {
  WAITING_FOR_REVIEW: 'נשלחה לבדיקת Apple וממתינה לתור',
  IN_REVIEW: 'בבדיקה אצל Apple',
  PENDING_DEVELOPER_RELEASE: 'אושרה על ידי Apple — ממתינה לשחרור שלך ב-App Store Connect',
  READY_FOR_SALE: 'אושרה ופורסמה ב-App Store',
  READY_FOR_DISTRIBUTION: 'אושרה ופורסמה ב-App Store',
  REJECTED: 'Apple דחתה את הגרסה — הסיבה מופיעה ב-App Store Connect',
  METADATA_REJECTED: 'Apple ביקשה לתקן את פרטי החנות ב-App Store Connect',
  DEVELOPER_REJECTED: 'הגשה בוטלה',
  FAILED: 'Apple לא קיבלה את ההגשה'
};

/** The single next thing the customer should do, from verified state only. */
export function nextIosStep(pkg) {
  if (!pkg) return 'LOADING';
  if (!pkg.available) return 'UNAVAILABLE';
  if (!pkg.apple?.connected) return 'CONNECT_APPLE';
  if (ACTIVE.has(pkg.state)) return 'BUILDING';
  if (pkg.state === 'READY' && pkg.mode === 'APP_STORE') {
    if (pkg.testflight?.state === 'PROCESSING') return 'APPLE_PROCESSING';
    if (pkg.testflight?.state === 'VALID') return pkg.review && pkg.review.state !== 'FAILED' ? 'IN_REVIEW' : 'TESTFLIGHT_READY';
    return 'BUILD_AGAIN';
  }
  return 'BUILD';
}

export function iosBuildLabel(pkg) {
  switch (pkg?.state) {
    case 'QUEUED': return 'בתור לבנייה...';
    case 'RUNNING': return 'בונה ובודק את אפליקציית האייפון (כ-15 דקות)...';
    case 'VERIFYING': return 'מאמת את קובץ האפליקציה...';
    case 'READY': return 'אפליקציית האייפון נבנתה ונבדקה';
    case 'FAILED': return 'בניית אפליקציית האייפון נכשלה — הקרדיטים הוחזרו';
    default: return '';
  }
}

export function reviewLabel(state) { return REVIEW_LABELS[state] || (state ? `מצב ב-Apple: ${state}` : ''); }

const ERROR_TEXT = {
  IOS_APP_RECORD_MISSING: 'צריך ליצור פעם אחת רשומת אפליקציה ב-App Store Connect (הוראות למטה).',
  IOS_DAILY_LIMIT: 'הגעת למספר בניות האייפון היומי. אפשר לנסות שוב מחר.',
  INSUFFICIENT_CREDITS: 'אין מספיק קרדיטים לבניית אייפון.',
  IOS_NOT_ENABLED: 'בניית אייפון עדיין לא זמינה.',
  IOS_PROVIDER_PLAN_NOT_COMMERCIAL: 'בניית אייפון עדיין לא זמינה.'
};

export default function IosAppPanel({ projectId }) {
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsAppRecord, setNeedsAppRecord] = useState(null);
  const [form, setForm] = useState({ issuerId: '', keyId: '', privateKey: '', fileName: '' });
  const [bundleId, setBundleId] = useState('');
  const [version, setVersion] = useState('');
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await api.iosPackageStatus(projectId);
      setPkg(data.iosPackage);
      setBundleId(current => current || data.iosPackage?.bundleId || '');
      setVersion(current => current || data.iosPackage?.version || '1.0.0');
    } catch (err) { setError(err.response?.data?.error || 'לא ניתן לטעון את מצב אפליקציית האייפון.'); }
  }, [projectId]);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);
  useEffect(() => {
    clearTimeout(timer.current);
    const step = nextIosStep(pkg);
    if (step === 'BUILDING' || step === 'APPLE_PROCESSING' || step === 'IN_REVIEW') timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [pkg, load]);

  if (!projectId) return null;
  const step = nextIosStep(pkg);

  const run = async (action) => {
    setBusy(true); setError('');
    try { await action(); } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'IOS_APP_RECORD_MISSING') setNeedsAppRecord(err.response.data.bundleId || bundleId);
      setError(ERROR_TEXT[code] || err.response?.data?.error || 'הפעולה נכשלה. נסו שוב.');
      if (err.response?.data?.iosPackage) setPkg(err.response.data.iosPackage);
    } finally { setBusy(false); }
  };

  const readKeyFile = (file) => {
    if (!file) return;
    if (file.size > 10000) { setError('קובץ המפתח צריך להיות קובץ ה-.p8 שהורדת מ-Apple.'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(current => ({ ...current, privateKey: String(reader.result || ''), fileName: file.name }));
    reader.readAsText(file);
  };

  const connect = () => run(async () => {
    await api.saveAppleConnection({ issuerId: form.issuerId.trim(), keyId: form.keyId.trim(), privateKey: form.privateKey });
    setForm({ issuerId: '', keyId: '', privateKey: '', fileName: '' });
    await load();
  });

  const build = () => run(async () => {
    setNeedsAppRecord(null);
    const data = await api.requestIosPackage(projectId, { bundleId: bundleId.trim(), version: version.trim() });
    setPkg(data.iosPackage);
  });

  const submit = () => run(async () => { const data = await api.submitIosToAppStore(projectId); setPkg(data.iosPackage); });

  return (
    <section className="android-package-panel" aria-live="polite" dir="rtl">
      <div className="android-package-head">
        <strong>אפליקציית אייפון (TestFlight ו-App Store)</strong>
        {pkg?.state && pkg.state !== 'NONE' && (
          <span className={`android-package-state is-${String(pkg.state).toLowerCase()}`}>
            {ACTIVE.has(pkg.state) && <span className="package-live-dot" aria-hidden="true"><span className="package-live-dot-core" /><span className="package-live-dot-ring" /></span>}
            {iosBuildLabel(pkg)}
          </span>
        )}
      </div>

      {step === 'LOADING' && <small>טוען...</small>}
      {step === 'UNAVAILABLE' && <small>בניית אפליקציית אייפון עדיין לא זמינה בחשבון הזה. בינתיים אפשר להוסיף את האתר למסך הבית (למטה).</small>}

      {step === 'CONNECT_APPLE' && (
        <div className="ios-connect">
          <p>כדי לבנות ולהתקין את האפליקציה באייפון, מחברים פעם אחת את חשבון Apple Developer שלך (בתשלום שנתי ל-Apple):</p>
          <ol>
            <li>ב-<a href="https://appstoreconnect.apple.com/access/integrations/api" target="_blank" rel="noopener noreferrer">App Store Connect ← Users and Access ← Integrations</a> יוצרים מפתח עם הרשאת <b>App Manager</b>.</li>
            <li>מעתיקים לכאן את ה-Issuer ID ואת ה-Key ID, ומעלים את קובץ ה-.p8 שירד.</li>
          </ol>
          <label>Issuer ID<input dir="ltr" value={form.issuerId} onChange={e => setForm({ ...form, issuerId: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" autoComplete="off" /></label>
          <label>Key ID<input dir="ltr" value={form.keyId} onChange={e => setForm({ ...form, keyId: e.target.value })} placeholder="ABC123DEFG" autoComplete="off" /></label>
          <label>קובץ המפתח (.p8)<input type="file" accept=".p8" onChange={e => readKeyFile(e.target.files?.[0])} /></label>
          {form.fileName && <small>נבחר: {form.fileName}</small>}
          <button type="button" className="btn submit-btn" disabled={busy || !form.issuerId || !form.keyId || !form.privateKey} onClick={connect}>{busy ? 'בודק מול Apple...' : 'חבר חשבון Apple'}</button>
          <small>המפתח נבדק מול Apple ונשמר מוצפן לחשבון שלך בלבד. אי אפשר לצפות בו שוב — רק להחליף או להסיר.</small>
        </div>
      )}

      {pkg?.apple?.connected && step !== 'UNAVAILABLE' && (
        <small>חשבון Apple מחובר (Key {pkg.apple.keyId}). <button type="button" className="link-btn" onClick={() => run(async () => { await api.removeAppleConnection(); await load(); })}>הסר או החלף</button></small>
      )}

      {(step === 'BUILD' || step === 'BUILD_AGAIN' || step === 'TESTFLIGHT_READY') && pkg?.apple?.connected && (
        <div className="ios-build">
          <label>מזהה האפליקציה (Bundle ID)<input dir="ltr" value={bundleId} onChange={e => setBundleId(e.target.value)} disabled={Boolean(pkg?.appStoreAppId)} /></label>
          <label>גרסה<input dir="ltr" value={version} onChange={e => setVersion(e.target.value)} /></label>
          <button type="button" className="btn submit-btn" disabled={busy} onClick={build}>{busy ? 'מתחיל...' : pkg?.state === 'READY' ? 'בנה גרסה חדשה' : 'צור אפליקציה לאייפון'}</button>
          <small>הבנייה רצה בנפרד מהאתר (כ-15 דקות) ונשלחת אוטומטית ל-TestFlight. התצוגה של האתר לא משתנה.</small>
        </div>
      )}

      {needsAppRecord && (
        <div className="ios-app-record">
          <p><b>צעד אחד ידני ש-Apple דורשת:</b> ב-<a href="https://appstoreconnect.apple.com/apps" target="_blank" rel="noopener noreferrer">App Store Connect ← Apps ← +  New App</a> יוצרים אפליקציה ובוחרים את ה-Bundle ID:</p>
          <code dir="ltr">{needsAppRecord}</code>
          <p>אם הוא לא מופיע ברשימה, רושמים אותו קודם ב-<a href="https://developer.apple.com/account/resources/identifiers/add/bundleId" target="_blank" rel="noopener noreferrer">Identifiers</a>. אחר כך לוחצים שוב על "צור אפליקציה לאייפון".</p>
        </div>
      )}

      {step === 'APPLE_PROCESSING' && <p>האפליקציה הועלתה ל-Apple ומעובדת שם (בדרך כלל 10–30 דקות). נעדכן כאן כשתהיה זמינה ב-TestFlight.</p>}
      {pkg?.testflight?.state === 'NOT_UPLOADED' && <p className="android-package-error">האפליקציה נבנתה, אבל ההעלאה ל-App Store Connect נכשלה. אפשר לבנות גרסה חדשה.</p>}
      {['FAILED', 'INVALID'].includes(pkg?.testflight?.state) && <p className="android-package-error">Apple לא אישרה את הקובץ לעיבוד. אפשר לבנות גרסה חדשה.</p>}

      {(step === 'TESTFLIGHT_READY' || step === 'IN_REVIEW') && (
        <div className="ios-testflight">
          <p>✅ גרסה {pkg.version} ({pkg.buildNumber}) זמינה ב-TestFlight. מתקינים את אפליקציית <a href="https://apps.apple.com/app/testflight/id899247664" target="_blank" rel="noopener noreferrer">TestFlight</a> באייפון ונכנסים עם אותו חשבון Apple.</p>
          {step === 'TESTFLIGHT_READY' && (
            <>
              <button type="button" className="btn" disabled={busy} onClick={submit}>{busy ? 'שולח...' : 'שלח לפרסום ב-App Store'}</button>
              <small>לפני השליחה ממלאים ב-App Store Connect תיאור, צילומי מסך ומדיניות פרטיות. אם משהו חסר, Apple תכתוב מה בדיוק.</small>
            </>
          )}
        </div>
      )}
      {pkg?.review?.state && <p><b>App Store:</b> {reviewLabel(pkg.review.state)}{pkg.review.error ? ` — ${pkg.review.error}` : ''}</p>}

      {pkg?.state === 'FAILED' && pkg.detail && <pre className="android-package-detail">{pkg.detail}</pre>}
      {error && <p className="android-package-error" role="alert">{error}</p>}
    </section>
  );
}
